import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';
import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { vehicleActions } from 'state/modules/vehicles';
import { debounce } from 'lodash';
import { truncateKeywordSpaces } from 'utils/utils';

const VehiclesTypesCategoriesSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  form: { errors, touched, setFieldValue },
  disabled,
  required,
}) => {
  const addNewCategoryValue = i18n.t('vehicle_types.add_new_category');
  const dispatch = useDispatch();
  const [defaultValue, setDefaultValue] = useState(null);
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });

  const { vehiclesTypeCategories, vehiclesTypeCategoriesLoading } = useSelector(
    (state) => state.vehicles
  );
  const [categories, setCategories] = useState([]);

  const fetchVehiclesTypeCategories = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      setParams(newParams);
      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }
      dispatch(vehicleActions.fetchVehicleTypeCategories(newParams));
    }, 1000),
    []
  );

  const prev = usePrevious(customParams);

  useEffect(() => {
    if (!value) return;
    fetchVehiclesTypeCategories({ ...params });
  }, []) // eslint-disable-line

  useEffect(() => {
    setCategories([{ category: addNewCategoryValue }, ...(vehiclesTypeCategories || [])]);
  }, [vehiclesTypeCategories]) // eslint-disable-line

  useEffect(() => {
    if (prev && customParams && JSON.stringify(prev) !== JSON.stringify(customParams)) {
      fetchVehiclesTypeCategories({ ...params, ...customParams });
    }
  }, [customParams]) // eslint-disable-line

  useEffect(() => {
    if (value || value === '' || value === addNewCategoryValue || !defaultValue) {
      const defaultVal = categories.find((el) => el.category === value);
      if (defaultVal) {
        setDefaultValue(defaultVal);
      } else {
        setDefaultValue(null);
      }
    }
  }, [value, categories]) // eslint-disable-line

  const handleFetchVehiclesTypeCategories = () => {
    if (categories.length === 0 && !vehiclesTypeCategoriesLoading && !params?.keyword)
      dispatch(vehicleActions.fetchVehicleTypeCategories(params));
  };

  return (
    <Autocomplete
      label={label}
      margin="normal"
      fullwidth="true"
      variant={variant}
      disabled={disabled}
      value={defaultValue}
      options={categories}
      required={required}
      clearOnBlur={false}
      id="vehiclesTypesCategoriesSelect"
      loading={vehiclesTypeCategoriesLoading}
      onHighlightChange={handleFetchVehiclesTypeCategories}
      onChange={(ev, val) => setFieldValue(name, val?.category || '')}
      error={errors && errors[name] && touched && touched[name]}
      getOptionSelected={(option, value) => option?.category === value?.category}
      onInputChange={(ev, val) =>
        val !== addNewCategoryValue && fetchVehiclesTypeCategories({ ...params, keyword: val })
      }
      helperText={errors && errors[name] && touched && touched[name] && errors[name]}
      getOptionLabel={(option) => option?.category || ''}
    />
  );
};

VehiclesTypesCategoriesSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  field: PropTypes.shape({
    value: PropTypes.any,
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
  }),
  form: PropTypes.shape({
    errors: PropTypes.object,
    touched: PropTypes.object,
  }),
  customParams: PropTypes.object,
  variant: PropTypes.oneOf(['standard', 'outlined', 'filled']),
};

VehiclesTypesCategoriesSelect.defaultProps = {
  required: true,
  disabled: false,
  form: {
    errors: {},
    touched: {},
  },
  customParams: {},
  variant: 'standard',
  label: 'Select...',
};

export default VehiclesTypesCategoriesSelect;
